// Servicio para llamar al backend local de Kokoro TTS (generación de voz en
// español). El backend corre en tu PC junto a Kokoro (Pinokio) y solo el admin
// usa este flujo desde el panel de administración.
const VITE = typeof import.meta !== 'undefined' ? (import.meta.env || {}) : {};
const API_URL = (VITE.VITE_KOKORO_API_URL || 'http://localhost:8500').replace(/\/$/, '');
const ADMIN_TOKEN = VITE.VITE_KOKORO_ADMIN_TOKEN || '';

const request = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Token': ADMIN_TOKEN,
      ...(options.headers || {}),
    },
  });
  const isBlob = options.isBlob && res.ok;
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch (_) { /* noop */ }
    throw new Error(detail);
  }
  return isBlob ? res.blob() : res.json();
};

const fileToText = async (file) => {
  const name = (file.name || '').toLowerCase();
  const buffer = await file.arrayBuffer();
  if (name.endsWith('.pdf')) {
    const pdfjsLib = await import('pdfjs-dist');
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    let out = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      out += (content.items || []).map((it) => it.str).join(' ') + '\n';
    }
    return out;
  }
  if (name.endsWith('.docx') || name.endsWith('.doc')) {
    const mammoth = await import('mammoth');
    const result = await mammoth.extractRawText({ arrayBuffer: buffer });
    return result.value;
  }
  // txt / md / cualquier texto plano
  return new TextDecoder('utf-8').decode(buffer);
};

// ── Estado y entorno del backend ──
export const getKokoroHealth = () => request('/health');

// ── Voces en español ──
export const getKokoroVoices = () => request('/api/tts/voices');

// ── Carpetas destino sugeridas (public/audios/...) ──
export const getKokoroDestinations = () => request('/api/tts/destination-folders');

// ── Preview: genera una muestra de audio y la devuelve como blob ──
export const previewKokoroAudio = async (payload) => {
  const res = await fetch(`${API_URL}/api/tts/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Token': ADMIN_TOKEN,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch (_) { /* noop */ }
    throw new Error(detail);
  }
  return res.blob();
};

// ── Generar y guardar el audio (asíncrono, por tareas con progreso) ──
// Inicia la generación; devuelve { task_id }. Consulta /api/tts/task/{id}.
export const startKokoroGeneration = (payload) =>
  request('/api/tts/generate', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// Estado/resultado de una tarea de generación.
export const getKokoroTask = (taskId) => request(`/api/tts/task/${encodeURIComponent(taskId)}`);

// Cancela una tarea en curso.
export const cancelKokoroTask = (taskId) =>
  request(`/api/tts/cancel/${encodeURIComponent(taskId)}`, { method: 'POST' });

// ── URL del archivo guardado (para reproducir/descargar por URL firmada con token) ──
export const kokoroFileUrl = (path) => {
  const params = new URLSearchParams({ path });
  return `${API_URL}/api/tts/file?${params.toString()}`;
};

// ── Regenerar localAudios.json (biblioteca de audios de la web) ──
export const rescanKokoroAudios = () =>
  request('/api/tts/rescan', { method: 'POST' });

// ──────────────────────────────────────────────────────────────────────────
// Limpieza / formateo del texto con IA local (LM Studio, igual que VortexDocs)
// Antes de mandar el texto a Kokoro, un modelo local corrige ortografía,
// quita símbolos raros/artefactos y deja la puntuación lista para lectura.
// ──────────────────────────────────────────────────────────────────────────

const LM_STUDIO_URL = (VITE.VITE_KOKORO_LMSTUDIO_URL || 'http://127.0.0.1:1234').replace(/\/$/, '');
const CLEAN_CHUNK_CHARS = 4000;

const CLEAN_SYSTEM_PROMPT =
  'Eres un editor experto en español que prepara textos para ser leídos en voz alta por un sintetizador de voz (TTS). Reglas:\n' +
  '1. Corrige la ortografía, tildes y puntuación en español SIN cambiar el contenido.\n' +
  '2. Elimina símbolos extraños, artefactos de OCR/Word/PDF, emojis, caracteres de control, pies de página, encabezados repetidos, asteriscos, guiones bajos y espacios dobles.\n' +
  '3. Elimina por completo los enlaces y URLs (como https://...).\n' +
  '4. Convierte las listas con viñetas o guiones (- o •) en oraciones fluidas con comas y punto y coma, conservando TODOS los puntos.\n' +
  '5. Reemplaza los guiones largos (—) y los incisos entre guiones por comas. Reemplaza las elipsis ("…") por ":" o punto final.\n' +
  '6. Expande abreviaturas para que se lean bien: "art."→"artículo", "núm."→"número", "pág."→"página", "p. ej."→"por ejemplo", "etc."→"etcétera", "cdo."→"cuando". Convierte símbolos como "%" en "por ciento" cuando sea necesario para evitar lecturas extrañas.\n' +
  '7. CONSERVA TODO el contenido: no elimines frases, no resumas, no parafrasees, no cambies el orden y no inventes texto. No colapses repeticiones.\n' +
  '8. Puntuación pensada para lectura en voz alta: termina cada oración con punto final; separa las cláusulas con coma o punto y coma; transforma paréntesis en comas cuando sea natural.\n' +
  '9. No agregues títulos, introducciones, comentarios ni texto adicional. Devuelve SOLO el texto corregido, sin formato markdown.';

// Modelo por defecto preferido para la limpieza/corrección de texto.
// Optimizado para RTX 5060 Ti (8 GB): el más accesible y veloz disponible.
const PREFERRED_CHAT_MODEL = 'bluerosesbutterfly/qwen2.5-7b-instruct';

const pickChatModel = (models) => {
  if (!Array.isArray(models) || models.length === 0) return '';
  // Prioridad 1: el modelo por defecto configurado (si está cargado en LM Studio).
  if (models.includes(PREFERRED_CHAT_MODEL)) return PREFERRED_CHAT_MODEL;
  // Prioridad 2: heurística (busca instruct/chat, descarta coder/vision/embedding…).
  return (
    models.find((m) => /(instruct|chat)/i.test(m) && !/(vl-7b|coder|code|kexer|vision)/i.test(m)) ||
    models.find((m) => !/(vl-7b|coder|code|kexer|vision|guard)/i.test(m)) ||
    models[0]
  );
};

// Comprueba si LM Studio está activo y devuelve el primer modelo de chat útil.
export const getLocalAIStatus = async () => {
  try {
    const res = await fetch(`${LM_STUDIO_URL}/v1/models`, { signal: AbortSignal.timeout(3000) });
    if (!res.ok) return { available: false, models: [], url: LM_STUDIO_URL };
    const data = await res.json();
    const models = (data?.data || [])
      .map((m) => m.id)
      .filter((id) => id && !id.toLowerCase().includes('embedding'));
    return { available: models.length > 0, models, chatModel: pickChatModel(models), url: LM_STUDIO_URL };
  } catch (_) {
    return { available: false, models: [], url: LM_STUDIO_URL };
  }
};

const callLMStudio = async (model, messages, maxTokens = 4096) => {
  const res = await fetch(`${LM_STUDIO_URL}/v1/chat/completions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      messages,
      max_tokens: maxTokens,
      temperature: 0.2,
    }),
    signal: AbortSignal.timeout(240000),
  }).catch((err) => {
    if (err?.name === 'TimeoutError' || err?.name === 'AbortError') {
      throw new Error('LM Studio tardó demasiado en responder (>4 min por chunk). Revisa que tenga el modelo cargado y no esté procesando otra solicitud.');
    }
    throw new Error(`No se pudo conectar con LM Studio: ${err.message}`);
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `LM Studio HTTP ${res.status}`);
  }
  const data = await res.json();
  const text = (data.choices?.[0]?.message?.content || '').trim();
  return text.replace(/```/g, '').replace(/^\s*"(.*)"\s*$/s, '$1').trim();
};

// Divide el texto en chunks sin cortar oraciones.
const splitCleanChunks = (text, max = CLEAN_CHUNK_CHARS) => {
  const chunks = [];
  const paragraphs = text.split(/\n{2,}/);
  let current = '';
  const flush = () => {
    const t = current.trim();
    if (t) chunks.push(t);
    current = '';
  };
  paragraphs.forEach((p) => {
    if (current && (current.length + p.length + 2) > max) flush();
    if (p.length > max) {
      flush();
      let rest = p.trim();
      while (rest.length > max) {
        let cut = rest.lastIndexOf('. ', max);
        if (cut < max / 2) cut = rest.lastIndexOf(' ', max);
        if (cut < max / 2) cut = max;
        chunks.push(rest.slice(0, cut + 1).trim());
        rest = rest.slice(cut + 1).trim();
      }
      current = rest;
    } else {
      current = current ? `${current}\n\n${p}` : p;
    }
  });
  flush();
  return chunks;
};

// Procesador genérico de chunks con concurrencia controlada y un pase de
// reintento para chunks fallidos. `caller(systemPrompt, userText)` es la función
// que ejecuta el modelo (Gemini o LM Studio). Devuelve el texto recombinado.
const cleanChunksWithWorker = async (text, caller, { concurrency = 3, onProgress } = {}) => {
  const chunks = splitCleanChunks(text);
  const total = chunks.length;
  const results = new Array(total);
  const errorsToRetry = new Set();
  let done = 0;
  let nextIdx = 0;

  const processChunk = async (index) => {
    try {
      results[index] = await caller(CLEAN_SYSTEM_PROMPT, chunks[index]);
    } catch (err) {
      // Chunk fallido (p. ej. saturado o cuota): se reintenta tras el resto.
      errorsToRetry.add(index);
    }
    done += 1;
    onProgress?.(done, total);
  };

  const worker = async () => {
    while (nextIdx < total) {
      const index = nextIdx;
      nextIdx += 1;
      await processChunk(index);
    }
  };

  const workers = Array.from({ length: Math.max(1, Math.min(concurrency, total)) }, worker);
  await Promise.all(workers);

  // Reintentos una única pasada (evita bucles infinitos si el servicio cae).
  if (errorsToRetry.size > 0) {
    for (const index of errorsToRetry) {
      try {
        results[index] = await caller(CLEAN_SYSTEM_PROMPT, chunks[index]);
      } catch (err) {
        // NUNCA se descarta contenido: si el chunk no se pudo limpiar (p. ej.
        // Gemini sin cuota, 429/503), se conserva el texto ORIGINAL tal cual
        // para no perder información del documento.
        results[index] = chunks[index];
      }
      done += 1;
      onProgress?.(done, total);
    }
  }

  return results.filter((p) => typeof p === 'string' && p.length > 0).join('\n\n');
};

// ── Limpieza con GEMINI (primaria) ──
// Llama a la Gemini Developer API (misma key/modelo que ya usa la app) con
// respuesta de TEXTO PLANO (sin forzar JSON). Tirador a ser usado primero.
const GEMINI_API_KEY = 'AIzaSyDmSpKfIkntT8HY2KzgZoXM8UoNRsNARc4';
const GEMINI_CHAT_MODEL = 'gemini-2.5-flash-lite';

const callGemini = async (system, user, maxTokens = 8192) => {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_CHAT_MODEL}:generateContent?key=${GEMINI_API_KEY}`;
  const body = {
    contents: [{ role: 'user', parts: [{ text: user }] }],
    generationConfig: { maxOutputTokens: maxTokens, temperature: 0.2 },
  };
  if (system) body.systemInstruction = { parts: [{ text: system }] };

  const exec = async () => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(240000),
    }).catch((err) => {
      if (err?.name === 'TimeoutError' || err?.name === 'AbortError') {
        throw new Error('Gemini tardó demasiado en responder (>4 min por chunk).');
      }
      throw new Error(`No se pudo conectar con Gemini: ${err.message}`);
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const msg = data?.error?.message || `Gemini HTTP ${res.status}`;
      const quota = new Error(`Gemini sin cuota o sobrecargado: ${msg}`);
      quota.retryable = res.status === 429 || res.status === 503 || res.status === 500 || /quota|exhausted|overload/i.test(msg);
      throw quota;
    }

    const data = await res.json();
    const text = (data.candidates?.[0]?.content?.parts || [])
      .map((p) => p.text || '')
      .join('')
      .trim();
    if (!text) throw new Error('Gemini devolvió respuesta vacía.');
    return text.replace(/```/g, '').replace(/^\s*"(.*)"\s*$/s, '$1').trim();
  };

  // Reintentos moderados con espera creciente para 429/503 (cuota/saturación),
  // sin replantear la llamada prolongada. Si siguen fallando, se lanza y el
  // chunk preserva su texto ORIGINAL (no se pierde contenido).
  let lastErr = new Error('Gemini sin respuesta.');
  for (let attempt = 0; attempt <= 2; attempt++) {
    try {
      return await exec();
    } catch (err) {
      lastErr = err;
      if (!err.retryable || attempt >= 2) break;
      await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
    }
  }
  throw lastErr;
};

// Limpia el texto con Gemini en chunks (concurrencia controlada).
// Lanza si Gemini no responde (sin cuota / sobrecarga / sin red).
export const cleanTextWithGemini = async (text, { concurrency = 3, onProgress } = {}) => {
  const out = await cleanChunksWithWorker(
    text,
    (system, user) => callGemini(system, user),
    { concurrency, onProgress }
  );
  if (!out) {
    throw new Error('Gemini no devolvió contenido (sin conexión, cuota agotada o sobrecarga).');
  }
  return out;
};

// Limpia el texto con el modelo local (LM Studio) como respaldo.
// Debe estar LM Studio corriendo en el puerto 1234 (como en Vortex Docs).
export const cleanTextWithLocalAI = async (
  text,
  { modelId, onProgress, concurrency = 3 } = {}
) => {
  const status = await getLocalAIStatus();
  if (!status.available) {
    throw new Error(
      `LM Studio no está disponible en ${LM_STUDIO_URL}. Ábrelo, carga un modelo y activa su servidor local (puerto 1234, como en Vortex Docs).`
    );
  }
  const model = modelId || pickChatModel(status.models);
  return cleanChunksWithWorker(
    text,
    (system, user) =>
      callLMStudio(model, [
        { role: 'system', content: system },
        { role: 'user', content: user },
      ]),
    { concurrency, onProgress }
  );
};

// Limpieza con fallback: GEMINI primero y, si falla, IA local (LM Studio).
export const cleanTextWithFallback = async (text, { modelId, concurrency = 3, onProgress } = {}) => {
  try {
    return await cleanTextWithGemini(text, { concurrency, onProgress });
  } catch (geminiErr) {
    console.warn('⚠️ Gemini falló, usando IA local (LM Studio):', geminiErr.message);
    let local;
    try {
      local = await cleanTextWithLocalAI(text, { modelId, concurrency, onProgress });
    } catch (localErr) {
      throw new Error(`Gemini no disponible (${geminiErr.message}) e IA local tampoco (${localErr.message}).`);
    }
    return local;
  }
};

export const tts = { fileToText };